import type { Level } from '../game/levels';
import type { RaceLevel } from '../game/race';
import { supabase } from './supabase';

// ─────────────────────────────────────────────────────────────────────────────
// The shop and the arcade (routes/student/Shop.tsx). The rules (prices, who
// gets paid) live in supabase/migrations/20261028_shop_and_games.sql;
// nothing here is a security boundary.
// ─────────────────────────────────────────────────────────────────────────────

/** Games are 2D levels (game/levels.ts). Older quiz and match games are no longer shown. */
export type GameKind = 'level' | 'quiz' | 'match';

export type ArcadeGame = {
  id: string; title: string; kind: GameKind; price: number; status: 'pending' | 'approved' | 'rejected' | 'removed'; note: string | null;
  plays: number; mine: boolean; created_at: string; earned: number | null; content: unknown;
  author: string | null; best: number | null; played: boolean;
  /** How many classmates have beaten it. */
  clears: number;
  /** 'race' for a race course (from the content; the arcade list doesn't include the content itself). */
  mode?: string;
};
export type ShopData = { balance: number | null; gear: string[]; look: unknown; games: ArcadeGame[] };

export const EGGS = [
  { id: 'mystery', name: 'Mystery egg', cost: 15, blurb: 'Any fish in the sea could hatch.' },
  { id: 'golden', name: 'Golden egg', cost: 45, blurb: 'Only rare, epic or legendary fish.' },
] as const;
export const MAKE_COST = 10;
/** A balance for the screen: the teacher's test student has endless sand dollars (20261051), shown as ∞. */
export const sandDollars = (n: number | null | undefined) => (n != null && n >= 999999 ? '∞' : String(n ?? 0));

function check<T>(res: { data: unknown; error: { message: string } | null }): T {
  if (res.error) throw new Error(res.error.message);
  return res.data as T;
}

export const fetchShop = async (cid: string) => check<ShopData>(await supabase.rpc('ocean_shop', { cid }));
export const buyEgg = async (cid: string, egg: string) =>
  check<{ species: string; rarity: string; new: boolean; balance: number }>(await supabase.rpc('ocean_buy_egg', { cid, egg }));
export const buyGear = async (cid: string, item: string) => check<{ balance: number }>(await supabase.rpc('ocean_buy_gear', { cid, item }));
/** Send a level to the teacher. Its price is the teacher's to set (on approving); the 2 sent here is ignored. */
export const makeGame = async (cid: string, title: string, level: Level | RaceLevel) =>
  check<{ id: string; balance: number }>(await supabase.rpc('ocean_game_create', { cid, title, kind: 'level', content: level, price: 2 }));
export const playGame = async (game: string) =>
  check<{ play: string; kind: GameKind; title: string; content: unknown; balance: number }>(await supabase.rpc('ocean_game_play', { game }));
/** A level's result: beaten (1 of 1) or not (0 of 1). Games never pay sand dollars (20261041). */
export const finishGame = async (play: string, won: boolean) =>
  check<{ refund: number }>(await supabase.rpc('ocean_game_finish', { play, score: won ? 1 : 0, total: 1 }));

// Teacher.
/** Approve (with the price to play, 🪙 1–5), turn down, or take down. Approving again changes the price. */
export const reviewGame = async (game: string, verdict: 'approved' | 'rejected' | 'removed', why = '', cost: number | null = null) =>
  check<void>(await supabase.rpc('ocean_game_review', { game, verdict, why, cost }));

// ── Games in progress (ocean_game_drafts, 20261034) ─────────────────────────
// The editors save the student's work-in-progress here as they go (besides
// their own browser), so the teacher can see what everyone is building.

export type GameDraft = { classroom_id: string; student_id: string; mode: 'level' | 'race'; title: string; content: unknown; updated_at: string };

export async function saveDraft(cid: string, sid: string, mode: GameDraft['mode'], title: string, content: unknown): Promise<void> {
  const { error } = await supabase.from('ocean_game_drafts')
    .upsert({ classroom_id: cid, student_id: sid, mode, title: title.slice(0, 60), content, updated_at: new Date().toISOString() });
  if (error) throw new Error(error.message);
}
export async function loadDraft(cid: string, sid: string, mode: GameDraft['mode']): Promise<GameDraft | null> {
  const { data } = await supabase.from('ocean_game_drafts').select('*').eq('classroom_id', cid).eq('student_id', sid).eq('mode', mode).maybeSingle();
  return (data as GameDraft | null) ?? null;
}
export async function classDrafts(cid: string): Promise<GameDraft[]> {
  return check<GameDraft[]>(await supabase.from('ocean_game_drafts').select('*').eq('classroom_id', cid).order('updated_at', { ascending: false }));
}
